import { makeObservable, observable, action, computed } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { CustomerTabState } from 'src/AppState/CustomerTabState/CustomerTabState';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { Map } from 'immutable';

interface AuditQueryType {
    from: string;
    to: string;
    page: number;
    topics: string[];
    tags: string[];
    perPage?: number;
}

interface QueryDataType {
    from?: string;
    to?: string;
    page?: number;
    topics?: string[];
    tags?: string[];
}

interface AuditUserType {
    id: number;
    name: string;
    type?: string;
}

interface AuditPropertyType {
    name: string;
    value: string;
}

interface AuditItemDataType {
    id: string | number;
    when: string;
    type: string;
    category: string;
    who: AuditUserType;
    body?: {
        properties?: AuditPropertyType[];
    };
}

export interface AuditItemType {
    get(key: 'id'): string | number;
    get(key: 'when'): string;
    get(key: 'type'): string;
    get(key: 'category'): string;
    get(key: string): string | number | Map<string, unknown>;
    toJS(): AuditItemDataType;
}

interface QueryMapType extends Map<string, unknown> {
    get(key: 'from'): string | undefined;
    get(key: 'to'): string | undefined;
    get(key: 'page'): number | undefined;
    get(key: 'perPage'): number | undefined;
    get(key: 'topics'): string[] | undefined;
    get(key: 'tags'): string[] | undefined;
}

interface AuditsMapType extends Map<string, unknown> {
    get(key: 'state'): string;
    get(key: 'query'): QueryMapType;
    get(key: 'items'): Map<string, AuditItemType>;
    get(key: 'errors'): unknown;
    get(key: 'perPage'): number | undefined;
    get(key: 'page'): number | undefined;
}

export interface AuditListStatePropsType {
    audits: AuditsMapType | Map<string, unknown>;
    total?: number;
    id?: string;
    query?: AuditsMapType;
    onQueryAuditsRequest: (id: string, query: AuditQueryType, filterTab: string | undefined) => void;
}

export class AuditListState {
    @observable public showModal = false;
    @observable public audit_id?: string = undefined;
    @observable public query: AuditQueryType;

    private getQueryData(audits: AuditsMapType | Map<string, unknown>): QueryDataType | null {
        const state = audits.get('state');
        if (state !== 'FINISHED') {
            return null;
        }

        const queryMap = audits.get('query');

        return {
            //@ts-expect-error
            from: queryMap.get('from'),
            //@ts-expect-error
            to: queryMap.get('to'),
            //@ts-expect-error
            page: queryMap.get('page'),
            //@ts-expect-error
            topics: queryMap.get('topics'),
            //@ts-expect-error
            tags: queryMap.get('tags'),
        };
    }

    public constructor(
        private readonly routerBackoffice: RouterBackoffice,
        private readonly customerTabState: CustomerTabState,
        private readonly models: ModelsState,
        initialProps: AuditListStatePropsType
    ) {
        makeObservable(this);

        const queryData = this.getQueryData(initialProps.audits);
        const defaultFrom = DateTime.current().subtractHours(48).format('YYYY-MM-DDT00:00:00');
        const defaultTo = DateTime.current().format('YYYY-MM-DDT23:59:59');

        this.query = {
            from: queryData?.from ?? defaultFrom,
            to: queryData?.to ?? defaultTo,
            page: queryData?.page ?? 1,
            topics: queryData?.topics ?? [],
            tags: queryData?.tags ?? [],
        };
    }

    @action public updateFromProps = (props: AuditListStatePropsType): void => {
        const queryData = this.getQueryData(props.audits);

        this.query = {
            page: queryData?.page ?? this.query.page,
            from: queryData?.from ?? this.query.from,
            to: queryData?.to ?? this.query.to,
            topics: queryData?.topics ?? this.query.topics,
            tags: queryData?.tags ?? this.query.tags,
        };
    };

    @action public openModal = (audit_id: string | number): void => {
        this.showModal = true;
        this.audit_id = String(audit_id);
    };

    @action public closeModal = (): void => {
        this.showModal = false;
        this.audit_id = undefined;
    };

    @action public changeInput = (
        field: keyof AuditQueryType,
        value: string | null,
        props: AuditListStatePropsType
    ): void => {
        const newValue = value ?? undefined;

        this.query = {
            ...this.query,
            page: 1,
            [field]: newValue,
        };

        this.loadAudit(props, this.query);
    };

    public canShowMore = (props: AuditListStatePropsType): boolean => {
        const { total, audits, query: propsQuery } = props;

        const items = audits.get('items');
        //@ts-expect-error
        const itemsSize = items?.size ?? 0;

        const perPage = propsQuery?.get('perPage') ?? 0;
        const page = propsQuery?.get('page') ?? 0;
        const queryPerPage = perPage > 0 && page > 0 ? perPage * page : 0;

        const hasMoreByTotal = typeof total === 'number' && itemsSize < total;
        const hasMoreByPagination = queryPerPage > 0 && itemsSize > 0 && queryPerPage === itemsSize;

        return hasMoreByTotal || hasMoreByPagination;
    };

    @action public showMore = (props: AuditListStatePropsType): void => {
        if (!this.canShowMore(props)) {
            return;
        }

        this.query.page++;
        this.loadAudit(props, this.query);
    };

    private loadAudit = (props: AuditListStatePropsType, query: AuditQueryType): void => {
        const id = this.routerBackoffice.getValueString('audits');
        if (id === '' || id === null) {
            return;
        }

        props.onQueryAuditsRequest(id.toString(), query, this.customerTabState.filterTab);
    };

    @computed public get showDifferentUserNameByAuditType() {
        return (user: { id: number; name: string; type?: string }, id: string): string => {
            if ((id.length > 0 && id.includes('accounts_update_account')) || user.id === 0) {
                return user.name;
            }
            if (user.type === 'staff') {
                const userData = this.models.getUserModel(user.id).basicStaffData.data;
                return userData?.name ?? user.name;
            }
            return user.name;
        };
    }
}
